import { observer } from 'mobx-react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import StyledSystemPropTypes from '@styled-system/prop-types';
import { Children, useRef, useState, useEffect, useCallback, forwardRef } from 'react';
import { useTrackImpression } from '@hooks/use-track-impression';
import { breakpoints } from '@constants/breakpoints';
import { colors } from '@constants/colors';
import { Flex } from '../../grid';
import { ChevronLeft } from '../../icons/chevron-left';
import { ChevronRight } from '../../icons/chevron-right';

const FullWidthContainer = styled.div`
  width: 100vw;
  position: relative;
  left: 50%;
  right: 50%;
  margin-left: -50vw;
  margin-right: -50vw;
`;

const BaseScrollingSnappingRow = styled(Flex)`
  display: flex;
  flex-direction: row;
  scroll-snap-type: x proximity;
  height: ${({ height }) => height};

  /* Hide scrollbar while maintaining scroll functionality */
  scrollbar-width: none; /* Firefox */
  -ms-overflow-style: none; /* Internet Explorer 10+ */

  &::-webkit-scrollbar {
    display: none; /* Safari and Chrome */
  }
`;

const FullWidthScrollingSnappingRow = styled(BaseScrollingSnappingRow)`
  /* For screens wider than 1440px, calculate dynamic padding to match Container behavior */
  @media (min-width: 1440px) {
    ${({ hasMaxWidth }) =>
      hasMaxWidth &&
      `
    padding-left: calc((100vw - 1440px) / 2 + 32px);
    padding-right: calc((100vw - 1440px) / 2 + 32px);
  `}
  }
`;

const ArrowContainer = styled.div`
  position: relative;
  display: flex;
  align-items: ${({ position }) => {
    if (position === 'top') return 'flex-start';
    if (position === 'bottom') return 'flex-end';
    return 'center';
  }};
  gap: 12px;

  @media (max-width: ${breakpoints.md}) {
    display: ${({ showOnMobile }) => (showOnMobile ? 'flex' : 'none')};
  }
`;

const ArrowButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  border: 1px solid black;
  background-color: transparent;
  color: ${colors.black};
  cursor: pointer;
  transition: all 0.2s ease;

  &:disabled {
    cursor: not-allowed;
    fill: ${colors.grey30};
    border-color: ${colors.grey30};
  }
`;

const OrderAtTableCarousel = forwardRef(
  ({ children, carouselHeight, impressionEvent, gap, width, px, pb, fullWidth, hasMaxWidth, ...rest }, ref) => {
    const count = Children.count(children);
    const impressionRef = useRef(null);
    useTrackImpression({ ref: impressionRef, eventName: impressionEvent });

    const ariaProps = count > 0 ? { role: 'region', 'aria-label': 'menu carousel' } : {};

    // Merge the forwarded ref with the impressionRef
    const mergedRef = useCallback(
      node => {
        impressionRef.current = node;
        if (typeof ref === 'function') {
          ref(node);
        } else if (ref && typeof ref === 'object' && 'current' in ref) {
          // eslint-disable-next-line no-param-reassign
          ref.current = node;
        }
      },
      [ref]
    );

    // If fullWidth is enabled, use the new container structure
    if (fullWidth) {
      return (
        <FullWidthContainer>
          <FullWidthScrollingSnappingRow
            {...ariaProps}
            hasMaxWidth={hasMaxWidth}
            px={hasMaxWidth ? { xs: '16px', md: '32px' } : { xs: '16px', xl: '32px' }}
            ref={mergedRef}
            height={carouselHeight}
            overflow="auto"
            touchAction="pan-x"
            width="100%"
            pb={pb}
          >
            <Flex gap={gap} flex="1 0 auto" minWidth="100%" {...rest}>
              {children}
            </Flex>
          </FullWidthScrollingSnappingRow>
        </FullWidthContainer>
      );
    }

    return (
      <BaseScrollingSnappingRow
        {...ariaProps}
        ref={mergedRef}
        height={carouselHeight}
        overflow="auto"
        touchAction="pan-x"
        gap={gap}
        width={width}
        px={px}
        pb={pb}
      >
        {children}
      </BaseScrollingSnappingRow>
    );
  }
);

OrderAtTableCarousel.propTypes = {
  children: PropTypes.any,
  carouselHeight: StyledSystemPropTypes.layout.height,
  impressionEvent: PropTypes.string,
  width: StyledSystemPropTypes.layout.width,
  gap: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  px: StyledSystemPropTypes.space.px,
  pb: StyledSystemPropTypes.space.pb,
  fullWidth: PropTypes.bool,
  hasMaxWidth: PropTypes.bool,
};

OrderAtTableCarousel.defaultProps = {
  children: null,
  carouselHeight: 'fit',
  impressionEvent: undefined,
  width: '100%',
  gap: 1,
  px: 0,
  pb: 0,
  fullWidth: false,
  hasMaxWidth: true,
};

const CarouselScrollArrows = ({
  carouselRef,
  position = 'center',
  showOnMobile = false,
  scrollDistance,
  className,
  disabled = false,
}) => {
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const checkScrollPosition = useCallback(() => {
    if (!carouselRef?.current) return;

    const element = carouselRef.current;
    const { scrollLeft, scrollWidth, clientWidth } = element;

    // Account for rounding errors
    const tolerance = 1;

    setCanScrollLeft(scrollLeft > tolerance);
    setCanScrollRight(scrollLeft < scrollWidth - clientWidth - tolerance);
  }, [carouselRef]);

  useEffect(() => {
    const element = carouselRef?.current;
    if (!element) {
      return undefined;
    }

    // Initial check
    checkScrollPosition();

    // Add scroll listener with debouncing
    let timeoutId;
    const handleScroll = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(checkScrollPosition, 10);
    };

    element.addEventListener('scroll', handleScroll, { passive: true });

    // Check on resize
    const handleResize = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(checkScrollPosition, 100);
    };

    window.addEventListener('resize', handleResize);

    return () => {
      element.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleResize);
      clearTimeout(timeoutId);
    };
  }, [carouselRef, checkScrollPosition]);

  const scrollLeft = useCallback(() => {
    if (!carouselRef?.current || !canScrollLeft) return;

    const element = carouselRef.current;
    const distance = scrollDistance || element.clientWidth * 0.8;

    element.scrollBy({
      left: -distance,
      behavior: 'smooth',
    });
  }, [carouselRef, canScrollLeft, scrollDistance]);

  const scrollRight = useCallback(() => {
    if (!carouselRef?.current || !canScrollRight) return;

    const element = carouselRef.current;
    const distance = scrollDistance || element.clientWidth * 0.8;

    element.scrollBy({
      left: distance,
      behavior: 'smooth',
    });
  }, [carouselRef, canScrollRight, scrollDistance]);

  // Hide arrows if there's nothing to scroll
  if (!canScrollLeft && !canScrollRight) {
    return <></>;
  }

  return (
    <ArrowContainer position={position} showOnMobile={showOnMobile} className={className}>
      <ArrowButton
        onClick={scrollLeft}
        disabled={disabled || !canScrollLeft}
        aria-label="Scroll carousel left"
        type="button"
      >
        <ChevronLeft height="20px" width="20px" />
      </ArrowButton>

      <ArrowButton
        onClick={scrollRight}
        disabled={disabled || !canScrollRight}
        aria-label="Scroll carousel right"
        type="button"
      >
        <ChevronRight height="20px" width="20px" />
      </ArrowButton>
    </ArrowContainer>
  );
};

CarouselScrollArrows.propTypes = {
  carouselRef: PropTypes.shape({
    current: PropTypes.object,
  }).isRequired,
  position: PropTypes.oneOf(['top', 'center', 'bottom']),
  showOnMobile: PropTypes.bool,
  scrollDistance: PropTypes.number,
  className: PropTypes.string,
  disabled: PropTypes.bool,
};

CarouselScrollArrows.defaultProps = {
  position: 'center',
  showOnMobile: false,
  scrollDistance: undefined,
  className: undefined,
  disabled: false,
};

const ComposedOrderAtTableCarousel = observer(OrderAtTableCarousel);
const ComposedCarouselScrollArrows = observer(CarouselScrollArrows);

export { ComposedOrderAtTableCarousel as OrderAtTableCarousel, ComposedCarouselScrollArrows as CarouselScrollArrows };
